import React, { useEffect, useState } from 'react';
import { noop } from 'Helpers/PropTypes';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import useAffordance from 'Hooks/useAffordance';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import ScoreBar from './ScoreBar';
import './Scores.css';

const PRIMARY_CREDIT_SCORE = 'Credit Score';
const SPOUSE_CREDIT_SCORE = 'Spouse Credit Score';
const LEVERAGE_SCORE = 'Leverage Score';
const DEBT_SCORE = 'Debt Score';
const WITHDRAWL_RATE = 'Withdrawal Rate';

function Scores({ toggleBlueprintLoaded }) {
  const [scores, setScores] = useState(null);
  const { householdResource } = useSimpleHousehold();
  const {
    collection,
  } = useAffordance(householdResource?.relationship('financialWellness').link('related'), {
    onFetch: toggleBlueprintLoaded,
  });

  useEffect(() => {
    if (collection) {
      const [financialWellnessResource] = collection;
      const componentsColl = financialWellnessResource?.attr('components');
      const wellnessItems = componentsColl?.resources().list() || [];
      setScores(wellnessItems);
    }
  }, [collection]);

  function renderScoreBars() {
    const wellnessScores = [];
    const creditScores = [];
    // We need to cap scores from above because display issues arise with the
    // score bar when values are very large (for example: NaN or 1.234e300).
    const fmtScore = (score, low, max) => {
      if (score > max) {
        return max + 1; // +1 to render the '+' character
      }
      return score;
    };

    scores
      .filter(
        score => !(
          score.attr('name') === LEVERAGE_SCORE
          && score.attributes.value === 0
        ),
      )
      .forEach((score) => {
        const name = score.attr('name');
        const valuesColl = score.attr('values');

        valuesColl.resources().list().forEach((valueResource) => {
          const label = valueResource.attr('label');
          const value = valueResource.attr('value');

          const scoreCalculations = valueResource.meta().field('scoreCalculations');
          const isPercentage = scoreCalculations.attr('hint') === 'percentage';
          const min = scoreCalculations.attr('min');
          const max = scoreCalculations.attr('max');

          const formatLowScore = isPercentage ? min / 100 : min;
          const formatHighScore = isPercentage ? max / 100 : max;
          let format = isPercentage ? '0.0%' : '0.0';

          if (name === PRIMARY_CREDIT_SCORE || name === SPOUSE_CREDIT_SCORE) {
            format = '0';
            if (value > 0) {
              creditScores.push(
                <div
                  key={name}
                  className={
                    name === PRIMARY_CREDIT_SCORE
                      ? 'user-credit-score'
                      : 'spouse-credit-score'
                  }
                >
                  <ScoreBar
                    lowScore={
                      isPercentage
                        ? `${min.toString()}%`
                        : min.toString()
                    }
                    highScore={
                      isPercentage
                        ? `${max.toString()}%`
                        : max.toString()
                    }
                    score={numeral(
                      fmtScore(value, formatLowScore, formatHighScore),
                    )
                      .format(format)
                      .toString()}
                    scoreLabel="Credit Score"
                    firstName={label}
                  />
                </div>,
              );
            }
          } else {
            wellnessScores.push(
              <ScoreBar
                lowScore={
                  isPercentage ? `${min.toString()}%` : min.toString()
                }
                highScore={
                  isPercentage ? `${max.toString()}%` : max.toString()
                }
                score={numeral(fmtScore(value, formatLowScore, formatHighScore))
                  .format(format)
                  .toString()}
                scoreLabel={name}
                flipGradient={name === DEBT_SCORE || name === WITHDRAWL_RATE}
                key={name}
              />,
            );
          }
        });
      });
    return (
      <>
        {wellnessScores}
        <div className="credit-scores">{creditScores}</div>
      </>
    );
  }

  return (
    <div className="scores" id="scores">
      <h3>Scores</h3>
      {scores && Array.isArray(scores) && renderScoreBars()}
    </div>
  );
}

Scores.propTypes = {
  toggleBlueprintLoaded: PropTypes.func,
};

Scores.defaultProps = {
  toggleBlueprintLoaded: noop,
};

export default Scores;
